import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Alert, Animated, Platform, View } from 'react-native';
import { IconBoldCalendar, IconCoins, IconTrash } from 'design-system/icons';
import { SettingsItem, SettingsItemTitle, SettingsItemContent, SettingsItemNavigate, SettingsItemDescription, } from 'design-system/SettingsItem';
import Banner from 'design-system/Banner';
import StatusBar from 'design-system/StatusBar';
import IconInfo from 'design-system/icons/IconInfo';
import IconTimer from 'design-system/icons/IconTimer';
import SectionHeader from 'design-system/SectionHeader';
import IconTransaction from 'design-system/icons/IconTransaction';
import { AppView, SettingsSection, Spacer, TextButtonLink } from 'design-system';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useFetchOld } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import useHeaderHeight from 'hooks/useHeaderHeight';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useStockMeta } from '../hooks';
import ActivityItem from '../components/ActivityItem';
import useDeleteRecurringBuy from '../hooks/useDeleteRecurringBuy';
import { formatNextBuyDate, getReadeablePeriodFromInterval } from '../utils';
import { getRecurringBuyActivities } from '../actions';
import { selectTradingAccount } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const RecurringBuyScreen = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const isMounted = useRef(true);
    const { navigateDeprecated } = navigation;
    const scrollY = useRef(new Animated.Value(0));
    const recurringBuy = route.params?.recurringBuy;
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const { id, assetId, period, nextBuy, amount } = recurringBuy;
    const tradingAccountObj = useAppSelector(selectTradingAccount);
    const tradingAccount = tradingAccountObj?.account;
    const [confirmDelete] = useDeleteRecurringBuy(id, assetId);
    useLayoutEffect(() => {
        const onDeletePress = () => {
            Alert.alert('Delete the Recurring Order', `Are you sure you want to delete this recurring order?`, [
                { style: 'cancel', text: 'Cancel' },
                {
                    text: 'Delete',
                    style: 'destructive',
                    onPress: () => {
                        confirmDelete();
                    },
                },
            ]);
        };
        navigation.setOptions({
            title: 'Recurring order',
            headerTintColor: colors.text.primary,
            headerRight: () => (<NavigationHeaderRightIconButton onPress={onDeletePress}>
          <IconTrash gradientColors={[colors.buttons.primary, colors.buttons.primary]}/>
        </NavigationHeaderRightIconButton>),
        });
    }, [colors.buttons.primary, colors.text.primary, confirmDelete]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const activities = useAppSelector((store) => store.unpersistedInvestReducer.historyForRecurringBuyId)?.[recurringBuy.id] || [];
    const headerHeight = useHeaderHeight();
    const stockCache = useStockMeta(assetId);
    const symbol = stockCache?.symbol;
    const [, , , fetch] = useFetchOld(muteAll);
    useEffect(() => {
        fetch(getRecurringBuyActivities(recurringBuy.id, { page: 1, perPage: 30 }));
        return () => {
            isMounted.current = false;
        };
    }, [recurringBuy.id]);
    const interval = useMemo(() => getReadeablePeriodFromInterval(period), [period]);
    const handleInterval = useCallback(() => {
        navigation.navigateDeprecated('InvestInterval', { recurringBuy });
    }, [recurringBuy]);
    const handleAmount = useCallback(() => {
        if (stockCache?.id) {
            navigation.navigateDeprecated('InvestBuyCustomAmount', {
                recurringBuy,
                assetId: stockCache.id,
            });
        }
    }, [recurringBuy, stockCache?.id]);
    const handleDeposit = useCallback(() => {
        if (scrambled)
            return;
        navigation.navigate('InvestSelectAccountToTopup');
    }, [scrambled, navigation]);
    const renderHeader = useMemo(() => (<>
        {tradingAccount?.availableToInvest?.value !== undefined &&
            tradingAccount.availableToInvest.value < amount.value && (<Banner noMarginBottom title="Pay attention" buttonTitle="Deposit" onPress={handleDeposit} icon={<IconInfo colorKey="brand"/>} description="There’s not enough funds to execute the next trade."/>)}
        <View style={styles.headerContainer}>
          <SettingsItem icon={<IconCoins alt width={rem(24)} height={rem(24)} color={colors.elements.secondary}/>}>
            <SettingsItemContent>
              <SettingsItemDescription>Stock name</SettingsItemDescription>
              <SettingsItemTitle>{stockCache?.name || symbol || ' '}</SettingsItemTitle>
            </SettingsItemContent>
          </SettingsItem>
          <SettingsItemNavigate description="Amount" onPress={handleAmount} icon={<IconTransaction gradientColors={colors.gradients.darkPurple.colors}/>} title={formatAmount(amount.value, amount.currency, 2)}/>
          <SettingsItemNavigate title={interval} description="Interval" onPress={handleInterval} icon={<IconTimer gradientKey="lightPink"/>}/>
          <SettingsItem icon={<IconBoldCalendar gradientKey="apricot"/>}>
            <SettingsItemContent>
              <SettingsItemDescription>Next execution</SettingsItemDescription>
              <SettingsItemTitle>{formatNextBuyDate(nextBuy)}</SettingsItemTitle>
            </SettingsItemContent>
          </SettingsItem>
        </View>
      </>), [
        tradingAccount?.availableToInvest?.value,
        amount.value,
        amount.currency,
        handleDeposit,
        styles.headerContainer,
        colors.elements.secondary,
        colors.gradients.darkPurple.colors,
        stockCache?.name,
        symbol,
        handleAmount,
        interval,
        handleInterval,
        nextBuy,
    ]);
    const handleSeeAllActivities = useCallback(() => {
        navigateDeprecated('InvestRecurringBuyActivity', { recurringBuy });
    }, [recurringBuy]);
    const renderHistory = useMemo(() => {
        if (!activities.length)
            return null;
        return (<>
        <SectionHeader horizontalMargins verticalMargins button={activities.length > 3 ? <TextButtonLink text="See all" onPress={handleSeeAllActivities}/> : null}>
          History
        </SectionHeader>
        <Spacer h={8}/>
        <SettingsSection noMarginTop>
          {activities.slice(0, 3).map((activity) => (<ActivityItem item={activity} key={activity.id}/>))}
        </SettingsSection>
      </>);
    }, [activities, handleSeeAllActivities]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    return (<AppView withFrame={false}>
      <StatusBar animated barStyle="dark-content"/>
      <View style={[styles.flex1, { paddingTop: headerHeight }]}>
        <Animated.ScrollView scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY.current } } }], {
            useNativeDriver: true,
        })} contentContainerStyle={paddingHorizontalStyle}>
          {renderHeader}
          <Spacer h={8}/>
          {renderHistory}
        </Animated.ScrollView>
      </View>
      <Animated.View style={[
            styles.navHeader,
            {
                height: headerHeight,
                ...Platform.select({
                    ios: {
                        shadowOpacity: scrollY.current.interpolate({
                            inputRange: [10, 30],
                            outputRange: [0, 1],
                            extrapolate: 'clamp',
                        }),
                    },
                    android: {
                        elevation: scrollY.current.interpolate({
                            inputRange: [10, 30],
                            outputRange: [0, 10],
                            extrapolate: 'clamp',
                        }),
                    },
                }),
            },
        ]}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    headerContainer: {
        marginTop: rem(16),
        borderRadius: rem(16),
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    navHeader: {
        top: 0,
        left: 0,
        right: 0,
        position: 'absolute',
        backgroundColor: colors.background.dark,
        shadowRadius: rem(32),
        shadowOpacity: 0,
        shadowColor: 'rgba(19, 48, 104, 0.12)',
        shadowOffset: { width: 0, height: rem(8) },
    },
    flex1: {
        flex: 1,
    },
}));
export default RecurringBuyScreen;
